import {Alert, FormControl, FormField} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {useGetCurrentUserInfoQuery, useGetUserQuery} from '@dms/api/customer';
import {useGetRolesQuery} from '@dms/api/security';
import {RoleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {MANAGER_ROLE_NAME} from '../constants/MANAGER_ROLE_NAME';
import {FormValues} from '../types/FormValues';

interface UserRolesProps extends RequiredTestIdProps {
  userId?: string | undefined;
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  isDisabled?: boolean;
}
export function UserRoles(props: UserRolesProps) {
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: roles, isLoading: isLoadingRoles} = useGetRolesQuery({});
  const {data: user} = useGetUserQuery({id: props.userId ?? ''}, {skip: !props.userId});

  const isCurrentUserManager = currentUser?.roles?.some((item) => item.title === MANAGER_ROLE_NAME);
  const isViewedUserManger = user?.roles?.some((role) => role.title === MANAGER_ROLE_NAME);
  const isManagerRoleLocked = !isCurrentUserManager && isViewedUserManger;
  const isInputDisabled = props.isDisabled || isManagerRoleLocked;

  // Only the manager lock empties the option list — that is what makes the
  // placeholder show `Manager` instead of the real value. A read-only form
  // (props.isDisabled) must keep its options, otherwise the assigned roles
  // have nothing to render against and the field falls back to `Select`.
  const roleOptions =
    roles
      ?.filter((item) => {
        if (isManagerRoleLocked) {
          return false;
        }

        if (!isCurrentUserManager && item.title === MANAGER_ROLE_NAME) {
          return false;
        }

        return true;
      })
      ?.map(rolesToOptions) ?? [];

  return (
    <VStack spacing={4}>
      <Show when={isManagerRoleLocked}>
        <Alert variant="info" message={i18n.t('page.settings.labels.managerRoleInfo')} />
      </Show>

      <FormField
        name="roles"
        control={props.control}
        type="multiChoice"
        isLoading={isLoadingRoles}
        isRequired
        isSearchable
        isNotClearable
        menuInPortal
        isDisabled={isInputDisabled}
        placeholder={isManagerRoleLocked ? MANAGER_ROLE_NAME : undefined}
        options={roleOptions}
        label={i18n.t('page.settings.labels.assignedRole')}
        helperText={i18n.t('entity.branch.labels.roleHelperText')}
        closeMenuOnSelect={false}
        data-testid={suffixTestId('userRoles-roles', props)}
      />
    </VStack>
  );
}

const rolesToOptions = (role: RoleResponseBody) => ({
  value: role.id,
  label: role.title,
});
